import React, { Component, ChangeEvent, ReactNode, FormEvent } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { AuthHelper } from '../../helpers/AuthHelper';
import Spinner from 'react-bootstrap/Spinner';
import { MIN_PASSWORD_LENGTH, PasswordValidInfo } from '../password-update-form/PasswordUpdateForm';

enum PasswordResetRequestState {
  NOT_SENT,
  PENDING,
  DONE,
  FAILED,
  CODE_EXPIRED,
  INVALID_PASSWORD,
  TOO_MANY_ATTEMPTS,
}

interface PasswordResetFormProps {
  code: string;
}

interface PasswordResetFormState {
  username: string;
  usernameError?: string;
  password: string;
  passwordError?: string;
  passwordRepeat: string;
  passwordRepeatError?: string;
  requestState: PasswordResetRequestState;
}

export class PasswordResetForm extends Component<PasswordResetFormProps, PasswordResetFormState> {
  private static readonly INPUT_USERNAME = 'username';
  private static readonly INPUT_NEW_PASSWORD = 'new-password';
  private static readonly INPUT_NEW_PASSWORD_REPEAT = 'new-password-repeat';

  constructor(props: PasswordResetFormProps) {
    super(props);
    this.state = {
      username: '',
      password: '',
      passwordRepeat: '',
      requestState: PasswordResetRequestState.NOT_SENT,
    };
  }

  handleInputChange = (event: ChangeEvent): void => {
    const { name, value } = (event.target as HTMLInputElement);
    switch (name) {
      case PasswordResetForm.INPUT_USERNAME:
        this.setState({ username: value });
        break;
      case PasswordResetForm.INPUT_NEW_PASSWORD:
        this.setState({ password: value });
        break;
      case PasswordResetForm.INPUT_NEW_PASSWORD_REPEAT:
        this.setState({ passwordRepeat: value });
        break;
    }
  };

  resetErrors = (): void => {
    this.setState({
      usernameError: undefined,
      passwordError: undefined,
      passwordRepeatError: undefined,
    });
  };

  validate = (username: string, password: string, passwordRepeat: string): boolean => {
    if (!username || username.length === 0) {
      this.setState({ usernameError: 'Vul je e-mailadres in' });
      return false;
    }
    if (!password || password.length === 0) {
      this.setState({ passwordError: 'Vul een nieuw wachtwoord in' });
      return false;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      this.setState({ passwordError:
          `Het wachtwoord dient minimaal ${MIN_PASSWORD_LENGTH} tekens lang te zijn` });
      return false;
    }
    if (password !== passwordRepeat) {
      this.setState({ passwordRepeatError: 'De ingevulde wachtwoorden komen niet overeen' });
      return false;
    }
    return true;
  };

  handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    this.resetErrors();

    const { username, password, passwordRepeat } = this.state;
    const isValid = this.validate(username, password, passwordRepeat);
    if (!isValid) {
      return;
    }

    const { code } = this.props;

    this.setState({
      requestState: PasswordResetRequestState.PENDING
    }, () => {
      AuthHelper.passwordForgotChange(code, username, password)
        .then(() => {
          this.setState({
            requestState: PasswordResetRequestState.DONE
          });
        })
        .catch((err) => {
          console.error('Failed to set password', err);

          switch (err.code) {
            case 'TooManyFailedAttemptsException':
            case 'TooManyRequestsException':
            case 'LimitExceededException':
              this.setState({ requestState: PasswordResetRequestState.TOO_MANY_ATTEMPTS });
              break;
            case 'InvalidPasswordException':
              this.setState({ requestState: PasswordResetRequestState.INVALID_PASSWORD });
              break;
            case 'ExpiredCodeException':
              this.setState({ requestState: PasswordResetRequestState.CODE_EXPIRED });
              break;
            default:
              this.setState({ requestState: PasswordResetRequestState.FAILED });
              break;
          }
        });
    });
  };

  render(): ReactNode {
    const { password, usernameError, passwordError, passwordRepeatError, requestState } = this.state;
    const stateDone = requestState === PasswordResetRequestState.DONE;

    return <>
      {
        !stateDone &&
        <Form className="form mb-3" onSubmit={this.handleSubmit}>
          <Form.Group className="form__item">
            <Form.Label>Gebruikersnaam</Form.Label>
            <Form.Control
              name={PasswordResetForm.INPUT_USERNAME}
              type="text"
              autoComplete="username"
              onChange={this.handleInputChange}
              isInvalid={!!usernameError}
            />
            {usernameError && <Alert variant="danger">{usernameError}</Alert> }
          </Form.Group>
          <Form.Group className="form__item">
            <Form.Label>Nieuw Wachtwoord</Form.Label>
            <Form.Control
              name={PasswordResetForm.INPUT_NEW_PASSWORD}
              type="password"
              autoComplete="new-password"
              onChange={this.handleInputChange}
              isInvalid={!!passwordError}
            />
            <Form.Text className="text-muted">
              <PasswordValidInfo password={password} />
            </Form.Text>
            {passwordError && <Alert variant="danger">{passwordError}</Alert> }

            <Form.Label>Herhaal Wachtwoord</Form.Label>
            <Form.Control
              name={PasswordResetForm.INPUT_NEW_PASSWORD_REPEAT}
              type="password"
              autoComplete="new-password"
              onChange={this.handleInputChange}
              isInvalid={!!passwordRepeatError}
            />
            {passwordRepeatError && <Alert variant="danger">{passwordRepeatError}</Alert> }
          </Form.Group>
          <Button
            className="form__submit"
            variant="primary" type="submit">{
              requestState === PasswordResetRequestState.PENDING ?
                <Spinner animation="border" role="status"/> :
                'Toepassen'
            }</Button>
        </Form>
      }
      { stateDone && <Alert variant="info">Jouw wachtwoord is gewijzigd</Alert> }
      { requestState === PasswordResetRequestState.FAILED && <Alert variant="danger">Jouw wachtwoord kon helaas niet gewijzigd worden</Alert> }
      { requestState === PasswordResetRequestState.CODE_EXPIRED && <Alert variant="danger">De gebruikersnaam klopt niet of de wachtwoordhersteltijd is verlopen. Vraag opnieuw een herstel link aan.</Alert> }
      { requestState === PasswordResetRequestState.INVALID_PASSWORD && <Alert variant="danger">Wachtwoord voldoet niet aan de wachtwoordeisen</Alert> }
      { requestState === PasswordResetRequestState.TOO_MANY_ATTEMPTS && <Alert variant="danger">Er zijn te veel pogingen gedaan in een korte tijd. Probeer het later opnieuw.</Alert> }
    </>;
  }
}
